import React, { useMemo, useState } from 'react';

import {
	EMOTION_TAGS,
	ENERGY_TAGS,
	GOSPEL_TAGS,
	GOSPEL_TAGS_SHORT,
	SONG_TYPE_TAGS,
} from '@/config/constants';
import { classNames } from '@/helpers/classNames';
import { useTeamSongs } from '@/providers/TeamSongsProvider';

import { ILayoutProps } from '../Layout';
import { Chip } from '../shared/Chip';
import { ModalSectionCard } from '../shared/ModalSectionCard';
import { PieChart } from '../shared/PieChart';
import { RadarChart, RadarChartData } from '../shared/RadarChart';
import { RadialBarChart } from '../shared/RadialBarChart';
import { RoundedButton } from '../shared/RoundedButton';
import { getSongThemeColor } from '../shared/SongThemeChip';
import { SmallSongCardProps } from '../SmallSongCard';
import { SmallTeamSongCardProps } from '../SmallTeamSongCard';
import { CreateChips } from './components/CreateChips';
import { CreateCollapsableSections } from './components/CreateCollapsableSections';
import { SectionTitle } from './components/SectionTitle';
import styles from './Songlist360Report.module.scss';

export enum FilterReportKeysEnum {
	EMOTION_TAGS = 'emotion_tags',
	GOSPEL_STORY = 'gospel_story',
	ENERGY = 'energy',
	SONG_TYPE = 'song_type',
}

type Songlist360ReportProps = {
	className?: string;
	chartClassName?: string;
	modalSectionClassName?: string;
	songIds: string[];
	filterReportKeys?: FilterReportKeysEnum[];
	onSongClick?: SmallTeamSongCardProps['onClick'];
	onViewMoreClick?: (tagCategory: string) => void;
};

export const Songlist360Report: React.FC<Songlist360ReportProps> = ({
	className,
	chartClassName,
	modalSectionClassName,
	songIds,
	filterReportKeys = Object.values(FilterReportKeysEnum),
	onSongClick,
	onViewMoreClick,
}) => {
	const { findTeamSongsByIdReduced } = useTeamSongs();
	const [showMoreOpened, setShowMoreOpened] = useState({
		emotion: false,
		gospel: false,
		energy: false,
		songType: false,
	});
	const songs = findTeamSongsByIdReduced(songIds);

	const emotionData = useMemo(() => {
		return EMOTION_TAGS.map((emotion) => {
			return {
				id: emotion.label,
				emotion: emotion.capitalisedLabel,
				totalStrength: songs.reduce((acc, song) => {
					const songEmotionStrength =
						song.analysisOutput?.emotions.find(
							(se) =>
								emotion.label.toLocaleLowerCase() ===
								se.emotion.toLocaleLowerCase(),
						)?.strength || 0;
					return acc + songEmotionStrength;
				}, 0),
				songs: songs.filter((song) =>
					song.analysisOutput?.emotions.find(
						(se) =>
							emotion.label.toLocaleLowerCase() ===
							se.emotion.toLocaleLowerCase(),
					),
				),
			};
		}).map((dataItem, i, dataArray) => {
			const highestStrength = Math.max(
				...dataArray.map((data) => data.totalStrength),
			);
			return {
				...dataItem,
				totalStrength: Math.max(
					0.2,
					0.2 + dataItem.totalStrength / highestStrength,
				),
			};
		});
	}, [songs]);

	// const emotionData = songs.reduce<RadarChartData>((acc, song) => {
	//   const songEmotionStrength =
	//   teamSong?.analysisOutput?.find(
	//     (songEmotion) => songEmotion.emotion === emotion.emotion,
	//   )?.strength || 0;

	// return {
	//   emotion: emotion.emotion,
	//   verse: emotion.strength || 0,
	//   song: songEmotionStrength,
	// };
	// 	return acc;
	// }, []);

	const gospelData = useMemo(() => {
		const unkownSongs = songs.filter((s) => !s.gospelTags.length);
		const godSongs = songs.filter((s) =>
			s.gospelTags.includes('glory of god'),
		);
		const sinSongs = songs.filter((s) =>
			s.gospelTags.includes('gravity of sin'),
		);
		const grandeurSongs = songs.filter((s) =>
			s.gospelTags.includes('grandeur of grace'),
		);

		return [
			{
				id: 'unknown',
				data: [
					{
						x: 'Unknown',
						y: unkownSongs.length,
					},
				],
				songs: unkownSongs,
			},
			{
				id: 'glory of god',
				data: [
					{
						x: 'God',
						y: godSongs.length,
					},
				],
				songs: godSongs,
			},
			{
				id: 'gravity of sin',
				data: [
					{
						x: 'Sin',
						y: sinSongs.length,
					},
				],
				songs: sinSongs,
			},
			{
				id: 'grandeur of grace',
				data: [
					{
						x: 'Grace',
						y: grandeurSongs.length,
					},
				],
				songs: grandeurSongs,
			},
			// If there are no 'unknown' songs then don't render the data at all
		];
		// .filter((item) =>
		// 	item.id === 'unknown' ? !!unkownSongs.length : true,
		// );
	}, [songs]);

	const energyData = useMemo(() => {
		const unkownSongs = songs.filter((s) => !s.energyTags.length);
		const upbeatSongs = songs.filter((s) =>
			s.energyTags.includes('upbeat'),
		);
		const buildingSongs = songs.filter((s) =>
			s.energyTags.includes('building'),
		);
		const settlingSongs = songs.filter((s) =>
			s.energyTags.includes('settling'),
		);
		const reflectiveSongs = songs.filter((s) =>
			s.energyTags.includes('reflective'),
		);

		return [
			{
				id: 'unknown',
				value: unkownSongs.length,
				songs: unkownSongs,
				color: getSongThemeColor('unknown').colorHex,
			},
			{
				id: 'upbeat',
				value: upbeatSongs.length,
				songs: upbeatSongs,
				color: getSongThemeColor('upbeat').colorHex,
			},
			{
				id: 'building',
				value: buildingSongs.length,
				songs: buildingSongs,
				color: getSongThemeColor('building').colorHex,
			},
			{
				id: 'settling',
				value: settlingSongs.length,
				songs: settlingSongs,
				color: getSongThemeColor('settling').colorHex,
			},
			{
				id: 'reflective',
				value: reflectiveSongs.length,
				songs: reflectiveSongs,
				color: getSongThemeColor('reflective').colorHex,
			},
		];
	}, [songs]);

	const songTypeData = useMemo(() => {
		const unkownSongs = songs.filter((s) => !s.songTypeTags.length);
		const chorusTypeSongs = songs.filter((s) =>
			s.songTypeTags.includes('chorus'),
		);
		const hymnSongs = songs.filter((s) => s.songTypeTags.includes('hymn'));

		return [
			{
				id: 'unknown',
				value: unkownSongs.length,
				songs: unkownSongs,
				color: getSongThemeColor('unknown').colorHex,
			},
			{
				id: 'chorus',
				value: chorusTypeSongs.length,
				songs: chorusTypeSongs,
				color: getSongThemeColor('chorus').colorHex,
			},
			{
				id: 'hymn',
				value: hymnSongs.length,
				songs: hymnSongs,
				color: getSongThemeColor('hymn').colorHex,
			},
		];
	}, [songs]);

	const colors = useMemo(() => {
		return [
			getSongThemeColor('unknown').colorHex,
			getSongThemeColor('god').colorHex,
			getSongThemeColor('sin').colorHex,
			getSongThemeColor('grace').colorHex,
		];
	}, []);

	return (
		<div className={classNames([styles['songlist360report'], className])}>
			{filterReportKeys?.includes(FilterReportKeysEnum.EMOTION_TAGS) && (
				<ModalSectionCard
					paddingSize="lg"
					className={modalSectionClassName}
				>
					<SectionTitle
						sideComponent={
							<RoundedButton
								onClick={() => {
									onViewMoreClick
										? onViewMoreClick('emotion')
										: setShowMoreOpened((state) => ({
												...state,
												emotion: !state.emotion,
										  }));
								}}
							>
								{showMoreOpened.emotion
									? 'Show less'
									: 'Show more'}
							</RoundedButton>
						}
					>
						Emotional Analysis
					</SectionTitle>

					<RadarChart
						data={emotionData}
						keys={['totalStrength']}
						indexBy="emotion"
						wrapperClassName={classNames([
							chartClassName,
							styles['radar'],
						])}
						maxValue={1.2}
						margin={{
							top: 30,
							left: 0,
							right: 0,
							bottom: 30,
						}}
					/>

					<CreateChips
						tags={EMOTION_TAGS}
						songMetricsData={emotionData}
					/>

					{showMoreOpened.emotion && (
						<CreateCollapsableSections
							onSongClick={onSongClick}
							allSongs={songs}
							songMetricsData={emotionData}
							tags={EMOTION_TAGS}
						/>
					)}
				</ModalSectionCard>
			)}

			{filterReportKeys?.includes(FilterReportKeysEnum.GOSPEL_STORY) && (
				<ModalSectionCard
					paddingSize="lg"
					className={modalSectionClassName}
				>
					<SectionTitle
						/* HIDE SHOW MORE - TO REFACTOR */
						/*sideComponent={
							<RoundedButton
								onClick={() => {
									setShowMoreOpened((state) => ({
										...state,
										gospel: !state.gospel,
									}));
									onViewMoreClick &&
										onViewMoreClick('gospel');
								}}
							>
								{showMoreOpened.gospel
									? 'Show less'
									: 'Show more'}
							</RoundedButton>
						}*/
						sideComponent={
							onViewMoreClick && (
								<RoundedButton
									onClick={() => {
										onViewMoreClick('gospel');
									}}
								>
									Show more
								</RoundedButton>
							)
						}
					>
						Gospel Story
					</SectionTitle>

					<RadialBarChart
						wrapperClassName={classNames([
							chartClassName,
							styles['radial-bar'],
						])}
						maxValue={songs.length}
						colors={colors}
						data={gospelData}
					/>

					<CreateChips
						tags={GOSPEL_TAGS_SHORT}
						songMetricsData={gospelData}
					/>

					{showMoreOpened.gospel && (
						<CreateCollapsableSections
							onSongClick={onSongClick}
							allSongs={songs}
							songMetricsData={gospelData}
							tags={GOSPEL_TAGS}
						/>
					)}
				</ModalSectionCard>
			)}

			{filterReportKeys?.includes(FilterReportKeysEnum.ENERGY) && (
				<ModalSectionCard
					paddingSize="lg"
					className={modalSectionClassName}
				>
					<SectionTitle
						sideComponent={
							<RoundedButton
								onClick={() => {
									onViewMoreClick
										? onViewMoreClick('energy')
										: setShowMoreOpened((state) => ({
												...state,
												energy: !state.energy,
										  }));
								}}
							>
								{showMoreOpened.energy
									? 'Show less'
									: 'Show more'}
							</RoundedButton>
						}
					>
						Energy
					</SectionTitle>

					<PieChart
						wrapperClassName={classNames([
							chartClassName,
							styles['radial-bar'],
						])}
						data={energyData}
					/>

					<CreateChips
						tags={ENERGY_TAGS}
						songMetricsData={energyData}
					/>

					{showMoreOpened.energy && (
						<CreateCollapsableSections
							onSongClick={onSongClick}
							allSongs={songs}
							songMetricsData={energyData}
							tags={ENERGY_TAGS}
						/>
					)}
				</ModalSectionCard>
			)}

			{filterReportKeys?.includes(FilterReportKeysEnum.SONG_TYPE) && (
				<ModalSectionCard
					paddingSize="lg"
					className={classNames([
						styles['last-section'],
						modalSectionClassName,
					])}
				>
					<SectionTitle
						sideComponent={
							<RoundedButton
								onClick={() => {
									onViewMoreClick
										? onViewMoreClick('songType')
										: setShowMoreOpened((state) => ({
												...state,
												songType: !state.songType,
										  }));
								}}
							>
								{showMoreOpened.songType
									? 'Show less'
									: 'Show more'}
							</RoundedButton>
						}
					>
						Song type
					</SectionTitle>

					<PieChart
						wrapperClassName={classNames([
							chartClassName,
							styles['radial-bar'],
						])}
						data={songTypeData}
					/>

					<CreateChips
						tags={SONG_TYPE_TAGS}
						songMetricsData={songTypeData}
					/>

					{showMoreOpened.songType && (
						<CreateCollapsableSections
							onSongClick={onSongClick}
							allSongs={songs}
							songMetricsData={songTypeData}
							tags={SONG_TYPE_TAGS}
						/>
					)}
				</ModalSectionCard>
			)}

			{/* <ModalSectionCard paddingSize="lg">
						<div
							className={classNames([
								styles['section-title'],
								styles['upgrade'],
							])}
						>
							<div className={styles['title']}>
								Upgrade for custom themes
							</div>
						</div>
					</ModalSectionCard> */}
		</div>
	);
};
